import React, { useState, useRef, useEffect } from "react";
import { Share2, Copy, Check, Circle, CheckCircle2, AlertTriangle, ChevronDown, ClipboardList, Send, ImageDown, Landmark } from "lucide-react";
import { toast } from "sonner";
import { PersonBadge } from "@/components/PersonBadge";
import { ReceiptCard } from "@/components/ReceiptCard";
import { getPayConfig, getMethod, payCurrencyOptions } from "@/lib/payments";
import { idr, money, buildBreakdownText, whatsappUrl, personBreakdown, computeInitialsMap, exportNodeAsImage, warmImageExport } from "@/lib/format";

const inputCls = "w-full h-11 rounded-lg border border-[var(--gz-line)] bg-transparent px-3 text-sm outline-none focus:border-[var(--gz-primary)] transition-colors";

function Seg({ options, value, onChange, testid }) {
  return (
    <div className="flex gap-1 p-1 rounded-lg bg-[var(--gz-line)]/50 mb-3" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.value}
          data-testid={`${testid}-${o.value}`}
          onClick={() => onChange(o.value)}
          className={`flex-1 h-9 rounded-md text-sm font-medium transition-colors ${value === o.value ? "bg-[var(--gz-primary)] text-white" : "text-[var(--gz-muted)]"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function PaymentEditor({ payment, receiptCurrency, onChange }) {
  const p = payment || {};
  const curOptions = payCurrencyOptions(receiptCurrency);
  const payCurrency = curOptions.includes(p.pay_currency) ? p.pay_currency : "IDR";
  const cfg = getPayConfig(payCurrency);
  const methodId = cfg.methods.some((m) => m.id === p.method) ? p.method : cfg.methods[0].id;
  const method = getMethod(payCurrency, methodId);

  const clearFields = { bank: null, account_number: null, bsb: null, handle: null };
  const setCurrency = (cur) => onChange({ ...clearFields, account_name: null, pay_currency: cur, method: getPayConfig(cur).methods[0].id });
  const setMethod = (id) => onChange({ ...clearFields, pay_currency: payCurrency, method: id });
  const setField = (f, raw) => {
    let v = raw;
    if (f.type === "num") v = f.key === "bsb" ? raw.replace(/[^\d-]/g, "") : raw.replace(/[^\d]/g, "");
    onChange({ pay_currency: payCurrency, method: methodId, [f.key]: v || null });
  };

  return (
    <div className="rounded-xl border border-[var(--gz-line)] p-4 mb-4" data-testid="payment-editor">
      <div className="flex items-center gap-1.5 mb-1 text-[var(--gz-ink)]">
        <Landmark size={16} className="text-[var(--gz-primary)]" />
        <span className="text-sm font-semibold">Everyone pays to (optional)</span>
      </div>
      <p className="text-xs text-[var(--gz-muted)] mb-3">One account for the whole bill. Shows on the shared link, image and copied text.</p>

      {curOptions.length > 1 && (
        <>
          <div className="text-xs font-medium text-[var(--gz-muted)] mb-1">Pay in</div>
          <Seg
            testid="pay-currency-seg"
            value={payCurrency}
            onChange={setCurrency}
            options={curOptions.map((c) => ({ value: c, label: `${getPayConfig(c).flag} ${c}` }))}
          />
        </>
      )}

      {cfg.methods.length > 1 && (
        <Seg
          testid="pay-method-seg"
          value={methodId}
          onChange={setMethod}
          options={cfg.methods.map((mm) => ({ value: mm.id, label: mm.label }))}
        />
      )}

      <div className="space-y-2">
        {method.fields.map((f) =>
          f.type === "bank" ? (
            <select
              key={f.key}
              data-testid={`pay-field-${f.key}`}
              value={p[f.key] || ""}
              onChange={(e) => onChange({ pay_currency: payCurrency, method: methodId, [f.key]: e.target.value || null })}
              className={`${inputCls} bg-[var(--gz-card)]`}
            >
              <option value="">{`Select ${f.label.toLowerCase()}…`}</option>
              {method.bankGroups
                ? method.bankGroups.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.banks.map((b) => <option key={b} value={b}>{b}</option>)}
                    </optgroup>
                  ))
                : (method.banks || []).map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          ) : (
            <input
              key={f.key}
              data-testid={`pay-field-${f.key}`}
              value={p[f.key] || ""}
              onChange={(e) => setField(f, e.target.value)}
              inputMode={f.type === "num" ? "numeric" : "text"}
              placeholder={f.placeholder || f.label}
              maxLength={f.key === "account_name" ? 40 : undefined}
              className={inputCls}
            />
          )
        )}
      </div>
    </div>
  );
}

function Row({ label, value, strong, accent }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className={`text-sm truncate ${strong ? "font-semibold" : "text-[var(--gz-muted)]"}`}>{label}</span>
      <span className={`tabular-nums shrink-0 whitespace-nowrap ${strong ? "text-lg font-semibold" : "text-sm"} ${accent ? "text-[var(--gz-primary)]" : ""}`}>
        {value}
      </span>
    </div>
  );
}

export default function SummarySection({ bill, summary, onTogglePaid, onShare, shareInfo, onPayment }) {
  const [open, setOpen] = useState({});
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const receiptRef = useRef(null);

  useEffect(() => { warmImageExport(); }, []);

  if (!summary) return null;
  const cur = summary.currency || "IDR";
  const dec = summary.decimals ?? 0;
  const m = (v) => money(v, cur);
  const people = summary.people || [];
  const imap = computeInitialsMap(people);

  const breakdownText = () =>
    buildBreakdownText({
      title: bill?.title,
      participants: bill?.participants || [],
      items: bill?.items || [],
      summary,
      url: shareInfo?.url,
      payment: bill?.payment,
    });

  const copyBreakdown = async () => {
    const text = breakdownText();
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Breakdown copied, paste it anywhere");
    } catch {
      toast.message("Copy this breakdown", { description: text });
    }
  };

  const waHref = whatsappUrl(breakdownText());
  const saveImage = async () => {
    if (!receiptRef.current) return;
    setSaving(true);
    try {
      const result = await exportNodeAsImage(
        receiptRef.current,
        `gigazip-${(bill?.title || "bill").replace(/\s+/g, "-").toLowerCase()}.png`
      );
      if (result === "shared") toast.success("Receipt image ready to share");
      else toast.success("Receipt image saved");
    } catch (e) {
      toast.error("Could not create the image.");
    } finally {
      setSaving(false);
    }
  };


  const doShare = async () => {
    setSharing(true);
    const info = await onShare();
    setSharing(false);
    if (info?.url) copy(info.url);
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.message("Copy this link", { description: url });
    }
  };

  const paidCount = people.filter((p) => p.paid).length;

  return (
    <div data-testid="summary-section" className="gz-rise">
      <h2 className="text-[15px] font-semibold mb-3">The breakdown</h2>

      {summary.has_unassigned && (
        <div data-testid="summary-unassigned" className="flex items-center gap-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 text-sm mb-4">
          <AlertTriangle size={18} />
          {m(summary.unassigned_amount)} of items are unassigned and not included in anyone's share.
        </div>
      )}

      <div className="rounded-xl border border-[var(--gz-line)] p-4 mb-4">
        <Row label="Subtotal" value={m(summary.subtotal)} />
        {summary.discount > 0 && <Row label="Discount" value={`− ${m(summary.discount)}`} />}
        {summary.service_amount > 0 && <Row label={`Service${summary.service_from_pct && summary.service_pct ? ` (${summary.service_pct}%)` : ""}`} value={m(summary.service_amount)} />}
        {summary.tax_amount > 0 && <Row label={`Tax${summary.tax_from_pct && summary.tax_pct ? ` (${summary.tax_pct}%)` : ""}`} value={m(summary.tax_amount)} />}
        {summary.rounding !== 0 && <Row label="Rounding" value={`${summary.rounding < 0 ? "− " : ""}${m(Math.abs(summary.rounding))}`} />}
        <div className="border-t border-[var(--gz-line)] mt-2 pt-2">
          <Row label="Grand total" value={m(summary.grand_total)} strong />
        </div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-[var(--gz-muted)]">Per person</h3>
        <span className="text-xs text-[var(--gz-muted)]">{paidCount}/{people.length} paid</span>
      </div>

      <div className="space-y-2 mb-5">
        {people.map((p) => {
          const isOpen = open[p.id];
          return (
            <div key={p.id} data-testid={`summary-person-${p.id}`} className={`rounded-xl border transition-colors ${p.paid ? "border-emerald-500/40 bg-emerald-500/5" : "border-[var(--gz-line)]"}`}>
              <div className="flex items-center gap-3 p-3">
                <PersonBadge person={p} size={40} label={imap[p.id]} />
                <button className="flex-1 min-w-0 text-left" onClick={() => setOpen((o) => ({ ...o, [p.id]: !o[p.id] }))} data-testid={`toggle-breakdown-${p.id}`}>
                  <div className="flex items-center gap-1 font-medium truncate">
                    {p.name}
                    <ChevronDown size={15} className={`text-[var(--gz-muted)] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                  <div className="text-xs text-[var(--gz-muted)]">{p.paid ? "Paid" : "Owes"}</div>
                </button>
                <div className="text-right shrink-0">
                  <div className="font-semibold tabular-nums whitespace-nowrap">{m(p.total)}</div>
                </div>
                <button
                  data-testid={`pay-toggle-${p.id}`}
                  onClick={() => onTogglePaid(p.id, !p.paid)}
                  aria-label={p.paid ? "Mark unpaid" : "Mark paid"}
                  className="grid place-items-center h-10 w-10 rounded-full active:scale-90 transition-transform"
                >
                  {p.paid ? <CheckCircle2 size={24} className="text-emerald-500" /> : <Circle size={24} className="text-[var(--gz-muted)]" />}
                </button>
              </div>
              {isOpen && (() => {
                const bd = personBreakdown(p, bill?.items || [], dec);
                return (
                  <div className="px-4 pb-3 pt-1 border-t border-[var(--gz-line)] text-sm gz-rise">
                    {bd.lines.map((l) => (
                      <Row key={l.id} label={l.name} value={m(l.amount)} />
                    ))}
                    <div className="border-t border-[var(--gz-line)] mt-1 pt-1">
                      <Row label="Items subtotal" value={m(bd.itemsSubtotal)} />
                      {bd.discount > 0 && <Row label="Discount" value={`− ${m(bd.discount)}`} />}
                      {bd.service > 0 && <Row label="Service" value={m(bd.service)} />}
                      {bd.tax > 0 && <Row label="Tax" value={m(bd.tax)} />}
                      {bd.rounding !== 0 && <Row label="Rounding" value={`${bd.rounding < 0 ? "− " : "+ "}${m(Math.abs(bd.rounding))}`} />}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>

      <PaymentEditor payment={bill?.payment} receiptCurrency={summary.currency} onChange={onPayment} />

      <button
        data-testid="share-btn"
        onClick={doShare}
        disabled={sharing}
        className="w-full h-13 py-3.5 rounded-full bg-[var(--gz-primary)] text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
      >
        <Share2 size={18} /> {sharing ? "Creating link…" : "Share breakdown"}
      </button>

      <div className="grid grid-cols-2 gap-2 mt-2">
        <button
          data-testid="copy-text-btn"
          onClick={copyBreakdown}
          className="h-12 rounded-full border border-[var(--gz-line)] font-medium flex items-center justify-center gap-2 active:scale-95 transition-transform"
        >
          <ClipboardList size={17} /> Copy as text
        </button>
        <a
          data-testid="whatsapp-btn"
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className="h-12 rounded-full border border-[var(--gz-line)] font-medium flex items-center justify-center gap-2 active:scale-95 transition-transform"
        >
          <Send size={16} /> WhatsApp
        </a>
      </div>

      <button
        data-testid="save-image-btn"
        onClick={saveImage}
        disabled={saving}
        className="mt-2 w-full h-12 rounded-full border border-[var(--gz-line)] font-medium flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
      >
        <ImageDown size={17} /> {saving ? "Saving…" : "Save as image"}
      </button>

      {shareInfo?.url && (
        <div data-testid="share-link-box" className="mt-3 rounded-xl border border-[var(--gz-line)] p-3 flex items-center gap-2">
          <span className="flex-1 text-xs text-[var(--gz-muted)] truncate">{shareInfo.url}</span>
          <button onClick={() => copy(shareInfo.url)} data-testid="copy-share-btn" className="grid place-items-center h-9 w-9 rounded-full bg-[var(--gz-primary)]/10 text-[var(--gz-primary)] active:scale-90 transition-transform">
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </button>
        </div>
      )}
      {shareInfo?.url && (
        <p className="text-center text-xs text-[var(--gz-muted)] mt-2">
          Anyone with this link can view the breakdown. It stays active.
        </p>
      )}

      {/* Off-screen capture target for "Save as image" */}
      <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }} aria-hidden="true">
        <ReceiptCard ref={receiptRef} title={bill?.title} summary={summary} participants={bill?.participants || []} items={bill?.items || []} payment={bill?.payment} />
      </div>
    </div>
  );
}
